import { type VariantProps, cva } from 'class-variance-authority';
import { Slot } from '@radix-ui/react-slot';
import * as React from 'react';

import { Loading } from '../icons';
import { cn } from '../../utils';

const buttonVariants = cva(
  `inline-flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap text-sm font-semibold ring-offset-background
  transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-default focus-visible:ring-offset-2
  [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0`,
  {
    variants: {
      variant: {
        primary:
          'bg-surface text-contrast hover:bg-surface-strong-hover active:bg-surface-strong-active',
        secondary:
          'bg-background text-surface border border-border-action hover:border-border-hover active:border-border-active',
        tertiary:
          'bg-surface-weak text-surface hover:bg-surface-weak-hover active:bg-surface-weak-active',
        ghost: 'text-weak hover:bg-surface-hover active:bg-surface-active',
        link: 'font-medium text-surface underline-offset-4 hover:underline hover:text-surface-strong-hover active:text-surface-strong-active',
        danger: 'bg-critical text-contrast hover:bg-critical/80 active:bg-critical/60',
      },
      size: {
        small: 'h-8 rounded-sm px-2',
        medium: 'h-10 rounded-md px-3',
        large: 'h-12 rounded-lg px-3',
      },
      disabled: { true: `pointer-events-none` },
      icon: { true: `self-center` },
      wide: { true: 'w-full' },
    },
    defaultVariants: {
      variant: 'primary',
      disabled: false,
      size: 'medium',
      icon: false,
      wide: false,
    },
    compoundVariants: [
      { icon: true, size: `small`, class: `w-8` },
      { icon: true, size: `medium`, class: `w-10` },
      { icon: true, size: `large`, class: `w-12` },

      { disabled: true, variant: 'primary', class: `bg-surface-disabled text-disabled` },
      { disabled: true, variant: 'secondary', class: `text-disabled border-disabled` },
      { disabled: true, variant: 'tertiary', class: `bg-surface-disabled text-disabled` },
      { disabled: true, variant: 'ghost', class: `bg-surface-disabled text-disabled` },
      { disabled: true, variant: 'link', class: `text-disabled` },
      { disabled: true, variant: 'danger', class: `bg-surface-disabled text-disabled` },
    ],
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  isLoading?: boolean;
  disabled?: boolean;
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      isLoading = false,
      asChild = false,
      className,
      disabled,
      children,
      variant,
      wide,
      size,
      icon,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, disabled, icon, wide, className }))}
        ref={ref}
        {...props}
      >
        {isLoading ? (
          <Loading className="animate-spin" />
        ) : (
          <span className="flex items-center gap-2">{children}</span>
        )}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants, Button };
